import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { FetchCustomerConfigService } from '../../utils/services/master/customer.service';
import { decryptText, encryptText, getCurrentCompanyUUID } from '../../utils/utils';
import { AppDispatch, RootState } from '../store';

interface CustomerConfigState {
    loading: boolean;
    customerConfig: any | null;
    error: string | null;
}

const initialState: CustomerConfigState = {
    loading: false,
    customerConfig: null,
    error: null,
};

export const fetchCustomerConfig = createAsyncThunk(
    'customerConfig/fetchCustomerConfig',
    async (_, { rejectWithValue }) => {
        try {
            const companyUUID = getCurrentCompanyUUID();
            if (!companyUUID) {
                return rejectWithValue('No company found');
            }
            const response = await FetchCustomerConfigService();
            if (response.code === 200 && response.data) {
                localStorage.setItem(`customerConfig_${companyUUID}`, encryptText(JSON.stringify(response.data)));
                return response.data;
            } else {
                return rejectWithValue(response.message || 'Failed to fetch customer config');
            }
        } catch (error) {
            return rejectWithValue('Error fetching customer config');
        }
    }
);

export const getCustomerConfig = () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const companyUUID = getCurrentCompanyUUID();

    if (state.customerConfig && state.customerConfig.customerConfig) {
        return state.customerConfig.customerConfig;
    }

    if (!companyUUID) {
        console.warn('No company UUID found for getCustomerConfig');
        return null;
    }

    const encryptedConfig = localStorage.getItem(`customerConfig_${companyUUID}`);
    if (encryptedConfig) {
        try {
            const decryptedConfig = encryptedConfig ? JSON.parse(decryptText(encryptedConfig)) : {};
            if (decryptedConfig) {
                dispatch(setCustomerConfig(decryptedConfig));
                return decryptedConfig;
            }
        } catch (error) {
            console.error('Decryption failed:', error);
        }
    }

    const result = await dispatch(fetchCustomerConfig());
    if (fetchCustomerConfig.fulfilled.match(result)) {
        return result.payload;
    } else {
        throw new Error(result.error.message || 'Failed to fetch customer config');
    }
};

const customerConfigSlice = createSlice({
    name: 'customerConfig',
    initialState,
    reducers: {
        setCustomerConfig: (state, action) => {
            state.customerConfig = action.payload;
            state.error = null;
        },
        resetCustomerConfig: (state) => {
            state.customerConfig = null;
            state.error = null;
            // localStorage.removeItem('customerConfig');
            const companyUUID = getCurrentCompanyUUID();
            if (companyUUID) {
                localStorage.removeItem(`customerConfig_${companyUUID}`);
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomerConfig.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCustomerConfig.fulfilled, (state, action) => {
                state.loading = false;
                state.customerConfig = action.payload;
                state.error = null;
            })
            .addCase(fetchCustomerConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setCustomerConfig, resetCustomerConfig } = customerConfigSlice.actions;
export default customerConfigSlice.reducer;